<template>
  <div>
    <modal @close="onCancel">
      <h3 slot="header">{{ template.name }} - {{ template.nameEng }}</h3>
      <div slot="body">
        <slot
          name="extra">
          <challenge-template-data
            :template="template"
          />
          <grid-row class="margin-top-xl margin-bottom-xl" >
            <grid-col col-size="2">
              <h3>{{ this.$t('health.challenge.startDate')}}</h3>
              <div class="filter flex">
                <vue-datepicker-local
                  class="medium-wide-size"
                  v-on:input="$emit('input', selectedDay)"
                  format="YYYY MMMM DD"
                  v-model="selectedDay"
                  :disabled-date="(date) => date <= new Date()"
                  :local="local"/>
              </div>
            </grid-col>

            <grid-col col-size="3">
              <h3>{{ this.$t('health.challenge.endDate')}}</h3>
              <div class="filter flex">
                <vue-datepicker-local
                  class="medium-wide-size"
                  v-on:input="$emit('input', selectedDay)"
                  format="YYYY MMMM DD"
                  v-model="endDate"
                  :disabled-date="(date) => date > new Date()"
                  :disabled="true"
                  :local="local"/>
              </div>
            </grid-col>
          </grid-row>

          <btn-pill
            :btnText="$t('health.challenge.launchChallenge')"
            v-on:click="onLaunchChallenge"
            btn-size="s"/>
        </slot>
      </div>
    </modal>
    <ok-cancel-modal
      v-if="showConfirmationModal"
      v-on:ok="onLaunchChallengeConfirm"
      v-on:cancel="onLaunchChallengeCancel"
      :title="launchChallengeMessage"
      :okButton="$t('commons.confirm')"
      :cancelButton="$t('commons.cancel')"/>
  </div>
</template>

<script>

import ChallengeTemplateData from 'BRAND/components/challenge/ChallengeTemplateData';
import Modal from '@/components/shared/dialogs/Modal';
import {mapGetters} from 'vuex';
import VueDatepickerLocal from 'vue-datepicker-local';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import localeData from 'dayjs/plugin/localeData';
import isoWeek from 'dayjs/plugin/isoWeek';
import GridRow from '@/components/shared/grid/GridRow.vue';
import GridCol from '@/components/shared/grid/GridCol.vue';
import BtnPill from '@/components/shared/buttons/BtnPill';
import OkCancelModal from '@/components/shared/dialogs/OkCancelModal';
import api from 'BRAND/api/challenge';

export default {
  components: {
    GridCol,
    GridRow,
    ChallengeTemplateData,
    Modal,
    VueDatepickerLocal,
    BtnPill,
    OkCancelModal
  },
  props: {
    template: {
      type: Object
    }
  },
  data () {
    return {
      selectedDay: new Date(),
      showConfirmationModal: false,
      launchChallengeMessage: this.$t('health.challenge.confirmLaunchMessage')
    };
  },
  created () {
    document.body.addEventListener('keyup', this.onKeyUp);
    this.selectedDay = new Date();
    this.selectedDay.setDate(this.selectedDay.getDate() + 1);
    this.setUpDatePicker();
  },
  destroyed () {
    document.body.removeEventListener('keyup', this.onKeyUp);
  },
  computed: {
    ...mapGetters([
      'getLanguageCode'
    ]),
    local () {
      return {
        dow: this.getLanguageCode === 'es' ? 1 : 0,
        hourTip: this.$t('filter.hour'), // tip of select hour
        minuteTip: this.$t('filter.minute'), // tip of select minute
        secondTip: this.$t('filter.second'), // tip of select second
        yearSuffix: '', // suffix of head year
        monthsHead: this.getMonths(), // months of head
        months: this.getMonthsShort(), // months of panel
        weeks: this.getWeekDays(), // weeks
        cancelTip: this.$t('filter.cancel'),
        submitTip: this.$t('filter.submit')
      };
    },
    endDate () {
      const result = new Date(this.selectedDay);
      result.setDate(result.getDate() + this.template.duration - 1);
      return result;
    }
  },
  methods: {
    onLaunchChallenge () {
      this.showConfirmationModal = true;
    },
    onLaunchChallengeConfirm () {
      const body = {
        'startDate': this.selectedDay,
        'templateId': this.template.id
      };
      this.showConfirmationModal = false;
      api.createChallenge(this, body).then(() => {
        this.$emit('save');
      }).catch(error => {
        this.$emit('error', error);
      });
    },
    onLaunchChallengeCancel () {
      this.showConfirmationModal = false;
    },
    onCancel (event) {
      this.$emit('close', event);
    },
    onKeyUp (event) {
      switch (event.keyCode) {
        case 27:
          this.$emit('close', event);
          break;
      }
    },
    setUpDatePicker () {
      dayjs.extend(localizedFormat);
      dayjs.extend(localeData);
      dayjs.localeData();
      dayjs.extend(isoWeek);
      window.addEventListener('keydown', (e) => {
        if (!this.listenKeyEvents) {
          return;
        }
        if (e.key === 'ArrowRight' && (this.selectedDay < new Date().setHours(0, 0, 0, 0))) {
          this.updateSelectedDate(1);
        } else if (e.key === 'ArrowLeft') {
          this.updateSelectedDate(-1);
        }
      });
    },
    getMonths () {
      const languageCode = this.getLanguageCode;
      dayjs.locale(languageCode);
      const months = dayjs.months();
      return months.map(month => this.capitalize(month));
    },
    getMonthsShort () {
      const languageCode = this.getLanguageCode;
      dayjs.locale(languageCode);
      const months = dayjs.monthsShort();
      return months.map(month => this.removeLastCharacter(this.capitalize(month)));
    },
    getWeekDays () {
      const languageCode = this.getLanguageCode;
      dayjs.locale(languageCode);
      const days = dayjs.weekdaysShort(true);
      return days.map(day => this.removeLastCharacter(this.capitalize(day)));
    },
    capitalize (word) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    },
    removeLastCharacter (str) {
      return str.substring(0, str.length - 1);
    },
    updateSelectedDate (days) {
      const newDate = new Date(this.selectedDay.getTime());
      newDate.setDate(this.selectedDay.getDate() + days);
      this.selectedDay = (newDate > new Date()) ? new Date() : newDate;
    }
  }
};
</script>
<style scoped lang="scss">
@import "../../../../stylesheets/variables.scss";
.filter {
  @media (max-width: $screen-l-max) {
    width: 100%;
    margin-bottom: $sp;
  }
}
</style>



// WEBPACK FOOTER //
// src/brand/health/components/challenge/LaunchChallengeModal.vue